import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * PRECISION MOTION — one shared, declarative motion layer.
 * Mark elements in normal-flow content with:
 *   data-mo="title"  → masked line reveal (power3.out, 0.08s line stagger)
 *   data-mo="image"  → container clip-path reveal bottom→top, inner <img> settles 16px
 *   data-mo="group"  → direct children fade up with a restrained stagger
 *   data-mo="fade"   → single element fade up
 *   data-mo-now      → animate on route entry instead of on scroll
 * NEVER place these on pinned / sticky ancestors or the 3D canvas.
 */

const START = 'top 85%';

function splitLines(el: HTMLElement): HTMLElement[] | null {
  // only split plain-text headings; anything else is animated as one line
  if (el.childElementCount > 0 || !el.textContent?.trim()) return null;
  const words = el.textContent.trim().split(/\s+/);
  el.textContent = '';
  const spans = words.map((w, i) => {
    const s = document.createElement('span');
    s.textContent = w;
    s.style.display = 'inline-block';
    el.appendChild(s);
    if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    return s;
  });
  const rows: string[][] = [];
  let top = -1;
  spans.forEach((s) => {
    if (Math.abs(s.offsetTop - top) > 2) { rows.push([]); top = s.offsetTop; }
    rows[rows.length - 1].push(s.textContent || '');
  });
  el.textContent = '';
  return rows.map((r) => {
    const mask = document.createElement('span');
    mask.setAttribute('data-mo-line', '');
    mask.style.cssText = 'display:block;overflow:hidden;padding-bottom:0.1em;margin-bottom:-0.1em;';
    const inner = document.createElement('span');
    inner.style.display = 'block';
    inner.textContent = r.join(' ');
    mask.appendChild(inner);
    el.appendChild(mask);
    return inner;
  });
}

export default function MotionLayer() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const root = document.querySelector('main');
    if (!root) return undefined;
    let cancelled = false;
    const restores: Array<() => void> = [];
    const mm = gsap.matchMedia();

    mm.add({ ok: '(prefers-reduced-motion: no-preference)', mobile: '(max-width: 767px)' }, (ctx) => {
      const { ok, mobile } = ctx.conditions as { ok: boolean; mobile: boolean };
      if (!ok) return undefined;
      const titles = gsap.utils.toArray<HTMLElement>('[data-mo="title"]', root);
      const images = gsap.utils.toArray<HTMLElement>('[data-mo="image"]', root);
      const groups = gsap.utils.toArray<HTMLElement>('[data-mo="group"]', root);
      const fades = gsap.utils.toArray<HTMLElement>('[data-mo="fade"]', root);
      const dy = mobile ? 10 : 14;
      const ty = mobile ? 16 : 24;

      // hide synchronously before paint → no flash; safety timer guarantees visibility
      gsap.set(titles, { autoAlpha: 0 });
      gsap.set(fades, { autoAlpha: 0, y: dy });
      groups.forEach((g) => gsap.set(g.children, { autoAlpha: 0, y: dy }));
      gsap.set(images, { clipPath: 'inset(100% 0% 0% 0%)' });
      const safety = window.setTimeout(() => {
        gsap.set([...titles, ...fades], { autoAlpha: 1, y: 0 });
        groups.forEach((g) => gsap.set(g.children, { autoAlpha: 1, y: 0 }));
      }, 4000);

      const when = (el: HTMLElement, play: () => void) => {
        if (el.hasAttribute('data-mo-now')) play();
        else ScrollTrigger.create({ trigger: el, start: START, once: true, onEnter: play });
      };

      fades.forEach((el) => when(el, () => gsap.to(el, { autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out', clearProps: 'transform' })));
      groups.forEach((g) => when(g, () => gsap.to(g.children, {
        autoAlpha: 1, y: 0, duration: 0.55, ease: 'power2.out', stagger: mobile ? 0.05 : 0.08, clearProps: 'transform',
      })));

      images.forEach((box) => {
        const img = box.querySelector('img');
        const play = () => {
          const go = () => {
            gsap.to(box, { clipPath: 'inset(0% 0% 0% 0%)', duration: mobile ? 0.8 : 0.95, ease: 'power3.out', clearProps: 'clipPath' });
            if (img && !mobile) gsap.fromTo(img, { y: 16 }, { y: 0, duration: 0.95, ease: 'power3.out', clearProps: 'transform' });
          };
          if (!img || img.complete) go();
          else { img.addEventListener('load', go, { once: true }); img.addEventListener('error', go, { once: true }); }
        };
        when(box, play);
      });

      // titles: split only after fonts are ready so line breaks are final
      const fontsReady = (document as Document & { fonts?: { ready: Promise<unknown> } }).fonts?.ready ?? Promise.resolve();
      fontsReady.then(() => {
        if (cancelled) return;
        titles.forEach((el) => {
          const original = el.textContent || '';
          const lines = splitLines(el);
          const restore = () => { if (el.querySelector('[data-mo-line]')) el.textContent = original; };
          restores.push(restore);
          gsap.set(el, { autoAlpha: 1 });
          const targets = lines || [el];
          gsap.set(targets, { y: ty, autoAlpha: lines ? 1 : 0 });
          when(el, () => gsap.to(targets, {
            y: 0, autoAlpha: 1, duration: 0.8, ease: 'power3.out', stagger: 0.08, clearProps: 'transform',
            // un-split afterwards so resizing reflows naturally
            onComplete: restore,
          }));
        });
        ScrollTrigger.refresh();
      });

      return () => window.clearTimeout(safety);
    });

    return () => {
      cancelled = true;
      mm.revert();
      restores.forEach((r) => r());
    };
  }, [pathname]);

  return null;
}